import { Controller } from "@hotwired/stimulus"

// Cards <-> table toggle for the index surfaces (Tasks, Processes).
//
// On phones the card list is the default and the Tabulator table is hidden
// (CSS: cards are `xl:hidden`, the table is `max-xl:hidden`). This controller
// adds an optional "Table"/"Cards" toggle, shown only below the xl breakpoint,
// that flips which one is visible and remembers the choice in localStorage.
// At xl+ the CSS already forces the table, so the toggle is inert there.
export default class extends Controller {
  static targets = ["cards", "table", "label"]
  static values = { key: { type: String, default: "index-view" } }

  connect() {
    this.desktop = window.matchMedia("(min-width: 1280px)")
    try { this.preferred = localStorage.getItem(this.keyValue) || "cards" } catch (_) { this.preferred = "cards" }
    this.boundResize = () => this.show(this.desktop.matches ? "table" : this.preferred)
    this.desktop.addEventListener("change", this.boundResize)
    this.boundInvalidate = () => {
      this.cardsStale = true
      if (this.current === "cards") this.loadCards()
    }
    this.changeEvents = ["task-actioned", "tasks-bulk-actioned", "process-table:refresh-requested"]
    this.changeEvents.forEach(name => window.addEventListener(name, this.boundInvalidate))
    this.boundResize()
  }

  disconnect() {
    this.desktop?.removeEventListener("change", this.boundResize)
    this.changeEvents?.forEach(name => window.removeEventListener(name, this.boundInvalidate))
  }

  // Force a deferred cards turbo-frame to fetch its source (mobile only).
  loadCards() {
    if (!this.hasCardsTarget) return
    const f = this.cardsTarget
    if (f.tagName !== "TURBO-FRAME") return
    if (f.getAttribute("src")) {
      if (this.cardsStale) { this.cardsStale = false; f.reload() }
      return
    }
    if (!f.dataset.indexViewSrc) return
    this.cardsStale = false
    f.setAttribute("loading", "eager")
    f.setAttribute("src", f.dataset.indexViewSrc)
  }

  toggle() {
    const next = this.current === "table" ? "cards" : "table"
    this.preferred = next
    this.show(next)
    try { localStorage.setItem(this.keyValue, next) } catch (_) {}
  }

  show(mode) {
    this.current = mode
    const showTable = mode === "table"
    if (this.hasCardsTarget) this.cardsTarget.classList.toggle("max-xl:hidden", showTable)
    if (this.hasTableTarget) {
      this.tableTarget.classList.toggle("max-xl:hidden", !showTable)
      // Tabulator mis-sizes while display:none — tell its controller to redraw.
      if (showTable) {
        this.tableTarget.dispatchEvent(new CustomEvent("index-view:reveal", { bubbles: true }))
      }
    }
    if (this.hasLabelTarget) this.labelTarget.textContent = showTable ? "Cards" : "Table"
    if (!showTable) this.loadCards()
  }
}
